import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService, AuthenticationService, ClientProfileService } from '../../_services';
import { Risk } from '../../_models/risk';
import { RiskService } from '../../_services/risk.service';
import { LoginHelper } from '../../helpers';

@Component({
  standalone: false,
  selector: "rizkListReport-component",
  templateUrl: 'rizkListReport.component.html',
  styleUrls: ['./rizkListReport.component.scss'],
})
export class RizkListReportComponent implements OnInit {
  risks: Risk[] = [];
  tenantName: string;
  token = LoginHelper.getFileToken();
  projectId: number | null = null;
  @ViewChild('riskTable') table: any;
  cols: any[] = [
    { field: 'dateRaised', header: 'Date Raised', width: '10%' },
    { field: 'name', header: 'Name', width: '25%' },
    { field: 'enterpriseWide', header: 'Enterprise Wide', width: '8%' },
    { field: 'businessUnit', header: 'Business Unit', width: '12%' },
    { field: 'score', header: 'Score', width: '8%' },
    { field: 'likelihood', header: 'Likelihood', width: '8%' },
    { field: 'severity', header: 'Severity', width: '8%' },
    { field: 'riskValue', header: 'Risk', width: '8%' },
    { field: 'finalDisposition', header: 'Disposition', width: '13%' },
  ];

  constructor(
    public riskService: RiskService,
    public router: Router,
    public route: ActivatedRoute,
    public alertService: AlertService,
    public clientProfileService: ClientProfileService,
    public authenticationService: AuthenticationService
  ) {
    // nothing special here
  }

  ngOnInit(): void {
    const tenant = (this.tenantName = LoginHelper.getTenantName());
    if (tenant == null || LoginHelper.getUserToken() == null) {
      this.authenticationService.logout();
      return;
    }

    const riskSetParam = this.route.snapshot.params['riskSet'];
    if (riskSetParam) {
      this.loadRisksForSet(riskSetParam);
    } else {
      this.loadAllRisks();
    }

    this.clientProfileService.getUserSetting('RizkListTable').subscribe((c: any) => {
      if (c && c.columnWidths) {
        for (let i = 0; i < c.columnWidths.length && i < this.cols.length; i++) {
          this.cols[i].width = c.columnWidths[i];
        }
      }
    });
  }

  loadRisksForSet(id: number) {
    this.riskService.getRisks(id).subscribe(
      (r: Risk[]) => {
        this.risks = r || [];
      },
      (error) => {
        this.alertService.error('An error occurred while loading risks for set.');
      }
    );
  }

  loadAllRisks() {
    this.risks = [];
    this.riskService.getAllRiskSets().subscribe(
      (sets: any[]) => {
        if (!sets || sets.length === 0) {
          this.risks = [];
          return;
        }
        sets.forEach((s) => {
          this.riskService.getRisks(s.riskSetId).subscribe(
            (rs: Risk[]) => {
              this.risks = this.risks.concat(rs || []);
            },
            () => {
              console.warn('Failed to load risks for set', s.riskSetId);
            }
          );
        });
      },
      (error) => {
        this.alertService.error('An error occurred while loading risk sets.');
      }
    );
  }

  exportAllCsv() {
    if (!this.risks || this.risks.length === 0) {
      return;
    }

    const headerKeys: string[] = [];
    this.risks.forEach((r) => {
      Object.keys(r).forEach((k) => {
        if (!headerKeys.includes(k)) headerKeys.push(k);
      });
    });

    // cSV rows
    const rows = this.risks.map((r) =>
      headerKeys.map((k) => this.csvSafe((r as any)[k])).join(',')
    );

    const csv = [headerKeys.join(','), ...rows].join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const filename = 'RisksReport.csv';
    const link = document.createElement('a');
    if ((link as any).download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  csvSafe(value: any) {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) return `"${value.toISOString()}"`;
    if (typeof value === 'object') {
      try {
        const s = JSON.stringify(value);
        return `"${s.replace(/"/g, '""')}"`;
      } catch {
        return '""';
      }
    }
    const s = String(value).replace(/"/g, '""');
    return `"${s}"`;
  }

  onColumnResize(event: any) {
    setTimeout(() => {
      try {
        const row = (this.table as any).tableViewChild.nativeElement.children[0].children[0];
        const width = row.clientWidth;
        const sizes: string[] = [];
        for (let i = 0; i < row.children.length - 2 && i < this.cols.length; i++) {
          sizes.push((row.children[i].clientWidth / width * 100) + '%');
        }
        this.clientProfileService.saveUserSetting('RizkListTable', { columnWidths: sizes }).subscribe(() => { });
      } catch (err) {
        console.warn('Could not persist column widths', err);
      }
    }, 300);
  }

  formatDate(d: any) {
    if (!d) return '';
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString();
    } catch {
      return String(d);
    }
  }

  // helper to limit name display length
  truncateName(s: string, max = 50) {
    if (!s) return '';
    if (s.length <= max) return s;
    return s.substring(0, max) + '...';
  }
}
